import { useEffect, useState } from "react";
import { Share2, Link2, Check, Mail, Instagram, ExternalLink } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  buildShareContent,
  copyToClipboard,
  INSTAGRAM_URL,
  openShareWindow,
  shareTargets,
} from "@/lib/share";

type Props = {
  name: string;
  slug: string;
  tagline?: string | null;
  bio?: string | null;
  imageUrl?: string | null;
  className?: string;
  url?: string;
};

const itemClass =
  "inline-flex w-full min-h-[44px] items-center justify-start gap-3 rounded-xl border border-border bg-background px-4 py-3 text-sm font-semibold text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background";

/**
 * "Share Her Story" — native share where the device supports it, plus an
 * explicit, predictable set of public share links. No OAuth, no credentials,
 * no posting on the user's behalf.
 */
const ShareStoryDialog = ({ name, slug, tagline, bio, imageUrl, className, url }: Props) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [canNativeShare, setCanNativeShare] = useState(false);

  const content = buildShareContent({ name, slug, tagline, bio, imageUrl, url });
  const targets = shareTargets(content);

  useEffect(() => {
    setCanNativeShare(typeof navigator !== "undefined" && typeof navigator.share === "function");
  }, []);

  useEffect(() => {
    if (!copied) return;
    const t = window.setTimeout(() => setCopied(false), 2500);
    return () => window.clearTimeout(t);
  }, [copied]);

  const nativeShare = async () => {
    try {
      await navigator.share({ title: content.title, text: content.text, url: content.url });
      setOpen(false);
    } catch {
      /* dismissed — keep the menu open so other options stay available */
    }
  };

  const copy = async () => {
    const ok = await copyToClipboard(content.url);
    setCopied(ok);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          type="button"
          className={
            className ??
            "inline-flex min-h-[44px] items-center justify-center gap-2 px-6 py-3 rounded-full bg-secondary-foreground/10 border border-secondary-foreground/25 text-sm font-semibold hover:bg-secondary-foreground/20 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-secondary"
          }
        >
          <Share2 className="w-4 h-4" aria-hidden />
          Share Her Story
        </button>
      </DialogTrigger>

      <DialogContent className="max-w-[min(92vw,26rem)] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="font-display uppercase text-xl break-words">Share her story</DialogTitle>
          <DialogDescription className="break-words">{content.text}</DialogDescription>
        </DialogHeader>

        <div className="grid gap-2">
          {canNativeShare && (
            <button type="button" onClick={nativeShare} className={itemClass}>
              <Share2 className="w-4 h-4 shrink-0" aria-hidden />
              Share via your device
            </button>
          )}

          {targets.map((t) =>
            t.key === "email" ? (
              <a key={t.key} href={t.href} className={itemClass}>
                <Mail className="w-4 h-4 shrink-0" aria-hidden />
                Email
              </a>
            ) : (
              <button
                key={t.key}
                type="button"
                onClick={() => openShareWindow(t.href)}
                className={itemClass}
              >
                <ExternalLink className="w-4 h-4 shrink-0" aria-hidden />
                {t.label}
              </button>
            ),
          )}

          <button type="button" onClick={copy} className={itemClass}>
            {copied ? (
              <Check className="w-4 h-4 shrink-0 text-primary" aria-hidden />
            ) : (
              <Link2 className="w-4 h-4 shrink-0" aria-hidden />
            )}
            {copied ? "Copied link" : "Copy link"}
          </button>
          <p role="status" aria-live="polite" className="sr-only">
            {copied ? "Copied link" : ""}
          </p>

          <div className="rounded-xl border border-dashed border-border p-3">
            <p className="text-xs text-muted-foreground break-words">
              Instagram doesn't allow posting from other sites. Copy the link above, then paste it into
              your story, caption or bio.
            </p>
            <button
              type="button"
              onClick={() => openShareWindow(INSTAGRAM_URL)}
              className="mt-2 inline-flex min-h-[44px] items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            >
              <Instagram className="w-4 h-4 shrink-0" aria-hidden />
              Open Instagram
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ShareStoryDialog;
